import React, { useEffect, useState } from 'react';
import { Modal, Button, Empty, List } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import STATUS from 'constants/status';
import dayjs from 'dayjs';
import { formatFullName } from 'utils/utils';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { getRequest } from 'api/apiClient';
import { DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import { OpSelect } from 'components/customAntd/DLS/OpSelect/OpSelect';
import { getStatusColor, profileIcon } from 'utils/visitorsHelper';
import { setSelectedVisit } from 'store/slices/visitsSlice';
import { VisitorSearch } from 'types/visitorSearchTypes';
import { useTranslation } from 'react-i18next';
import { hasPermission } from 'utils/utils';

interface VisitorOption {
    label: string;
    value: string;
    visitId: number;
    visitorId: number;
    firstName: string;
    lastName: string;
    timestamp: string;
}

interface SignInModalProps {
    open: boolean;
    locationIdRef: number;
    onClose: () => void;
    openVisitorsDrawer: () => void;
    openSignInManuallyDrawer: () => void;
    visitorNameFilter?: {
        firstName: string;
        middleName: string;
        lastName: string;
    };
}

const SignInModal: React.FC<SignInModalProps> = ({ open, locationIdRef, onClose, openVisitorsDrawer, openSignInManuallyDrawer, visitorNameFilter }) => {
    const dispatch: AppDispatch = useDispatch();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasAllvisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:w');

    const [visitorOptions, setVisitorOptions] = useState<VisitorOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const { t } = useTranslation();

    // Fetch visitor options
    useEffect(() => {
        const fetchVisitorOptions = async () => {
            setLoading(true);
            try {
                const visitors = await getRequest(`/orgs/${orgId}/search/visitors`, {
                    siteId: locationIdRef,
                    startDate: dayjs().startOf('day').format(DATE_TIME_FORMAT),
                    endDate: dayjs().endOf('day').format(DATE_TIME_FORMAT)
                });

                const filteredPendingVisitors = visitors.data
                    .filter((visitor: VisitorSearch) => visitor.status === STATUS.PENDING.id)
                    .filter((visitor: VisitorSearch) => {
                        // When 'All Visitor' scope is OFF, only show visitors assigned to the logged-in user
                        if (hasAllvisitorsWrite) return true;
                        return visitor.hostUserId === globalUserId;
                    })
                    .filter((visitor: VisitorSearch) => {
                        // Apply visitor name filter if provided
                        if (!visitorNameFilter) return true;

                        const firstNameMatch = visitorNameFilter.firstName === "NONE" || visitor.firstName?.toLowerCase() === visitorNameFilter.firstName.toLowerCase();
                        const lastNameMatch = visitorNameFilter.lastName === "NONE" || visitor.lastName?.toLowerCase() === visitorNameFilter.lastName.toLowerCase();

                        return firstNameMatch && lastNameMatch;
                    })
                    .map((visitor: VisitorSearch) => ({
                        label: formatFullName(visitor.firstName, visitor.middleName, visitor.lastName),
                        value: `${visitor.visitId}-${visitor.id}`,
                        visitId: visitor.visitId,
                        visitorId: visitor.id,
                        firstName: visitor.firstName,
                        lastName: visitor.lastName,
                        timestamp: visitor.timestamp
                    }));

                // If visitorNameFilter is not provided, add the "Add New Visitor" option
                if (!visitorNameFilter && filteredPendingVisitors.length > 0) {
                    filteredPendingVisitors.push({
                        label: t('signInModal.addVisitorSpace'),
                        value: 'add-new-visitor',
                        visitId: -1,
                        visitorId: -1,
                        visit: {},
                        style: {
                            color: 'var(--colorPrimary)',
                        },
                    });
                }

                setVisitorOptions(filteredPendingVisitors);
            } catch (error) {
                console.log("Failed to fetch visitor options.");
            } finally {
                setLoading(false);
            }
        };
        fetchVisitorOptions();
    }, [orgId, locationIdRef, visitorNameFilter, t, globalUserId, hasAllvisitorsWrite]);

    const handleOptionSelect = async (value: string, option: VisitorOption) => {
        if (value === 'add-new-visitor') {
            openSignInManuallyDrawer();
        } else {
            const selectedVisit = await getRequest(`/orgs/${orgId}/visitor/${option.visitId}`);
            dispatch(setSelectedVisit(selectedVisit.data[0]));
            openVisitorsDrawer();
        }
        onClose();
    };

    return (
        <Modal
            title={t('signInModal.title')}
            open={open}
            onCancel={onClose}
            footer={null}
            centered
        >
            <OpSpace direction="vertical" size="large" style={{ display: 'flex', paddingTop: '10px' }}>
                {visitorNameFilter && visitorOptions.length > 1 ? (
                    <List
                        itemLayout="horizontal"
                        dataSource={visitorOptions}
                        renderItem={item => {
                            return (
                                <List.Item
                                    onClick={() => handleOptionSelect(item.value, item)}
                                    style={{ cursor: 'pointer', transition: 'background-color 0.3s' }}
                                >
                                    <List.Item.Meta
                                        avatar={profileIcon({ firstName: item.firstName || '', lastName: item.lastName || '', size: 40, color: getStatusColor("Pending") })}
                                        title={item.label}
                                        description={dayjs(item.timestamp).format(DATE_TIME_AM_PM_FORMAT)}
                                    />
                                </List.Item>
                            );
                        }}
                        style={{ overflowY: 'auto', maxHeight: '50vh' }}
                    />
                ) : (
                    <OpSelect
                        testId="sign-in-visitor-select"
                        options={visitorOptions}
                        placeholder={t('signInModal.placeholder')}
                        onChange={(value, option) => handleOptionSelect(value as string, option as VisitorOption)}
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        loading={loading}
                        style={{ width: '100%' }}
                        notFoundContent={
                            <Empty description={t('signInModal.noVisitor')}>
                                <Button
                                    type="primary"
                                    style={{ width: '100%' }}
                                    onClick={() => {
                                        openSignInManuallyDrawer();
                                        onClose();
                                    }}
                                >
                                    {t('signInModal.addVisitor')}
                                </Button>
                            </Empty>
                        }
                    />
                )}
            </OpSpace>
        </Modal>
    );
};

export default SignInModal;
